import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Cta,
  CtaRow,
  Divider,
  DrillRow,
  Helper,
  OptionRow,
  SearchField,
  SectionTitle,
  SegmentedControl,
  SegmentedControlItem,
  Sheet,
  SheetBody,
  SheetDrill,
  SheetFooter,
  SheetHeader,
  SwitchRow,
} from '../../../../design-system/components/mobile';
import { routes } from '../../../../routes/routes';
import {
  useCatalogGroupInfo,
  useTcgCatalogs,
  useTcgCategories,
  useTcgRarities,
} from './facetData';
import {
  CLEARED_FILTERS,
  CONDITIONS,
  countAppliedFilters,
  selectTcgCategory,
} from './filterModel';

/**
 * The mobile filter sheet: the whole facet set, as the library drew it.
 *
 * THIS IS NOT "THE RAIL IN A SHEET". The rail is a 256px column that is always
 * on screen, so it can afford to expand every facet at once and let the eye
 * skip. A sheet is 89% of a phone and it is modal — the same flat stack there
 * is a scroll through forty rows to reach Condition, with the CTA row pinned
 * over the bottom of it the whole way. The mobile track designed a different
 * shape for exactly this, and every part of it already exists:
 *
 *   `DrillRow`     48px disclosure into a sub-panel, with an ink applied count
 *   `SheetDrill`   the panel stack, sliding right-to-left in and back out
 *   `SheetHeader`  `drill` variant: back button, title, count, no trailing slot
 *   `SearchField`  `insheet` — the CSS comment names its source as the SET
 *                  DRILL, which is the panel it is used in here
 *   `SwitchRow`    the boolean filter row
 *   `SegmentedControl`  a small closed set, chosen in place
 *   `OptionRow`    44px radio/checkbox row, the only control inside a panel
 *
 * WHAT THE SHEET DOES THAT THE RAIL CANNOT
 * ----------------------------------------
 * SETS ARE BROWSABLE. The rail hands you a "Browse by set" button that leaves
 * the page for `/sets`, which on a phone means losing the sheet, the scroll
 * position and every filter you had already set, to come back through a URL.
 * Here a set is a searchable list inside the sheet, off the real
 * `tcg-category-catalogs` endpoint, and choosing one returns you to the root
 * panel with the choice showing on the row. Sets are the facet buyers actually
 * use — "Evolving Skies, near mint" is how a card is asked for — so it is the
 * one that most deserved the drill.
 *
 * WHAT IS NOT HERE, AND WHY
 * -------------------------
 * The PRICE facet. The library ships `Histogram` for it and the sheet has the
 * room, but there is no price-distribution endpoint to draw the bars from and
 * `priceRange` is `[0, 1000]` by default while the grid returns R21,500 cards.
 * See the long note in `filterModel.js`. A price control has to wait for that
 * decision; a slider drawn over an unknown is a control that lies.
 *
 * APPLYING IS LIVE, as it has always been on this page: every tap updates the
 * parent's filters and the grid behind the scrim refetches, which is what makes
 * "Show 62 results" in the footer a true statement rather than a guess. The
 * footer's solid CTA therefore CLOSES, it does not commit.
 */

/** Panel indices for `SheetDrill`. Root is 0 and every drill returns to it. */
const PANEL = { root: 0, game: 1, set: 2, rarity: 3, condition: 4 };

export function MarketplaceFilterSheet({
  open,
  onClose,
  filters,
  onFilterChange,
  onClearAll,
  totalCards = 0,
  loading = false,
}) {
  /* THE CONTENT IS A SEPARATE COMPONENT SO IT DOES NOT EXIST UNTIL THE SHEET
     DOES. `Sheet` returns null while closed, so a child component is never
     mounted and its effects never run — which is what keeps the three facet
     endpoints off the critical path of a mobile marketplace load. Hoisting the
     hooks into this wrapper would fire `tcg-categories`, and the whole
     catalog-group lookup cascade behind a `?catalog_group=` URL, on every
     visit whether or not a buyer ever taps Filters.

     It also means panel and search state reset on close for free, with no
     effect watching `open` to undo them. */
  return (
    <Sheet open={open} onClose={onClose}>
      <FilterSheetPanels
        filters={filters}
        onFilterChange={onFilterChange}
        onClearAll={onClearAll}
        onClose={onClose}
        totalCards={totalCards}
        loading={loading}
      />
    </Sheet>
  );
}

function FilterSheetPanels({ filters, onFilterChange, onClearAll, onClose, totalCards, loading }) {
  const [panel, setPanel] = useState(PANEL.root);
  const [setQuery, setSetQuery] = useState('');
  /* The set list is the one facet nobody pays for unless they ask: it is a
     request per game and hundreds of rows. Latched rather than tied to the
     current panel so stepping out and back in does not refetch it. */
  const [setsWanted, setSetsWanted] = useState(false);

  const tcgId = filters?.tcg_category_id || '';
  const { tcgCategories, loadingCategories } = useTcgCategories();
  const { rarities, loadingRarities } = useTcgRarities(tcgId);
  const { catalogs, loadingCatalogs } = useTcgCatalogs(setsWanted ? tcgId : '');
  const { catalogGroupInfo, loadingCatalogGroup } = useCatalogGroupInfo(
    filters?.catalog_group_id,
    tcgId,
    tcgCategories,
  );

  const appliedCount = countAppliedFilters(filters);
  const selectedRarities = Array.isArray(filters?.rarity) ? filters.rarity : [];
  const selectedConditions = Array.isArray(filters?.condition) ? filters.condition : [];

  /* FOCUS FOLLOWS THE PANEL, and it has to be moved by hand.
     `Sheet` moves focus in on open and traps it, but a drill swaps the whole
     panel underneath that trap: the element that had focus is unmounted, focus
     falls to <body>, and the next Tab escapes the sheet entirely. So drilling
     in lands on the sub-panel's back button and returning lands back on the row
     that opened it — which is also the correct spatial answer, not just the
     accessible one.

     The back button is reached through the header node because `SheetHeader`
     forwards its ref to the header element and builds the button itself; it is
     the first control in that header, so `querySelector('button')` is it. */
  const rowRefs = useRef({});
  const headRefs = useRef({});
  const previousPanel = useRef(PANEL.root);

  useEffect(() => {
    const from = previousPanel.current;
    previousPanel.current = panel;
    if (from === panel) return;
    if (panel === PANEL.root) {
      const row = rowRefs.current[from];
      if (row && row.focus) row.focus();
      return;
    }
    const head = headRefs.current[panel];
    const back = head && head.querySelector('button');
    if (back) back.focus();
  }, [panel]);

  const update = (key, value) => onFilterChange({ ...filters, [key]: value });

  const toggleMulti = (key, option) => {
    const current = Array.isArray(filters?.[key]) ? filters[key] : [];
    update(
      key,
      current.includes(option) ? current.filter((item) => item !== option) : [...current, option],
    );
  };

  /* Choosing a game drops the rarity and the set with it — both belong to one
     game, see `selectTcgCategory`. Re-tapping the game already chosen is a
     no-op rather than a clear, because a buyer confirming their choice has not
     asked to lose the set underneath it. */
  const chooseGame = (value) => {
    if (String(value) !== String(tcgId)) onFilterChange(selectTcgCategory(filters, value));
    setPanel(PANEL.root);
  };

  const chooseSet = (catalogGroupId) => {
    update('catalog_group_id', catalogGroupId);
    setPanel(PANEL.root);
  };

  const openPanel = (index) => () => {
    if (index === PANEL.set) setSetsWanted(true);
    setPanel(index);
  };
  const back = () => setPanel(PANEL.root);

  const gameName = (() => {
    if (!tcgId) return 'All games';
    const match = tcgCategories.find((tcg) => String(tcg.id) === String(tcgId));
    return match ? match.name || match.title : loadingCategories ? 'Loading' : 'Selected';
  })();

  const setName = (() => {
    if (!filters?.catalog_group_id) return 'All sets';
    if (loadingCatalogGroup) return 'Loading';
    return catalogGroupInfo ? catalogGroupInfo.setName : filters.catalog_group_id;
  })();

  /* The list is hundreds of sets per game, which is why this panel is the one
     with a search field. Matching is on the set name only: the id is a slug
     nobody types. */
  const query = setQuery.trim().toLowerCase();
  const visibleCatalogs = query
    ? catalogs.filter((catalog) =>
        String(catalog.catalog_group || '').toLowerCase().includes(query),
      )
    : catalogs;

  const resultsLabel = loading
    ? 'Show results'
    : `Show ${Number(totalCards).toLocaleString('en-ZA')} ${totalCards === 1 ? 'result' : 'results'}`;

  return (
    <>
      <SheetDrill index={panel}>
        {/* ------------------------------------------------------- root */}
        <>
          {/* No ref: the root header has no back button, and returning to it
              focuses the drill row you came from instead. */}
          <SheetHeader title="Filters" appliedCount={appliedCount} />
          <SheetBody>
            {/* The four drills are wrapped so `.ds-drill-row:last-of-type`
                lands on the fourth of them. Unwrapped, the last BUTTON in this
                body is the availability switch row, so Condition kept its
                hairline and sat one pixel above the divider below it. */}
            <div>
              <DrillRow
                ref={(node) => {
                  rowRefs.current[PANEL.game] = node;
                }}
                label="Game"
                value={gameName}
                onClick={openPanel(PANEL.game)}
              />
              <DrillRow
                ref={(node) => {
                  rowRefs.current[PANEL.set] = node;
                }}
                label="Set"
                value={setName}
                onClick={openPanel(PANEL.set)}
              />
              <DrillRow
                ref={(node) => {
                  rowRefs.current[PANEL.rarity] = node;
                }}
                label="Rarity"
                value={selectedRarities.length ? undefined : 'Any'}
                count={selectedRarities.length || undefined}
                onClick={openPanel(PANEL.rarity)}
              />
              <DrillRow
                ref={(node) => {
                  rowRefs.current[PANEL.condition] = node;
                }}
                label="Condition"
                value={selectedConditions.length ? undefined : 'Any'}
                count={selectedConditions.length || undefined}
                onClick={openPanel(PANEL.condition)}
              />
            </div>

            <Divider />

            {/* Three mutually exclusive values with short labels: a segmented
                control shows all of them and the current one in 38px, where a
                drill would spend a whole panel on a choice of three. */}
            <SectionTitle as="h3" size="sm">
              Product type
            </SectionTitle>
            <SegmentedControl>
              <SegmentedControlItem
                active={!filters?.productType}
                onClick={() => update('productType', '')}
              >
                Everything
              </SegmentedControlItem>
              <SegmentedControlItem
                active={filters?.productType === 'card'}
                onClick={() => update('productType', 'card')}
              >
                Cards
              </SegmentedControlItem>
              <SegmentedControlItem
                active={filters?.productType === 'sealed'}
                onClick={() => update('productType', 'sealed')}
              >
                Sealed
              </SegmentedControlItem>
            </SegmentedControl>

            <Divider />

            {/* A checkbox in the rail, a switch here. Same filter, and the
                switch is the library's row for a boolean that changes what the
                page shows the moment it moves. The sub-label says what turning
                it OFF does, because "Available to buy" alone does not tell a
                buyer that sold-out cards are being hidden from them. */}
            <SwitchRow
              label="Available to buy"
              sub="Off shows sold-out cards too"
              checked={Boolean(filters?.available_to_buy)}
              onCheckedChange={(next) => update('available_to_buy', next)}
            />
          </SheetBody>
        </>

        {/* ------------------------------------------------------- game */}
        <>
          <SheetHeader
            drill
            title="Game"
            backLabel="Back to filters"
            onBack={back}
            ref={(node) => {
              headRefs.current[PANEL.game] = node;
            }}
          />
          <SheetBody>
            {loadingCategories ? (
              <Helper>Loading games</Helper>
            ) : (
              <>
                <OptionRow
                  control="radio"
                  label="All games"
                  inputProps={{
                    name: 'tcgCategory',
                    checked: !tcgId,
                    onChange: () => chooseGame(''),
                  }}
                />
                {tcgCategories.map((tcg) => (
                  <OptionRow
                    key={tcg.id}
                    control="radio"
                    label={tcg.name || tcg.title}
                    inputProps={{
                      name: 'tcgCategory',
                      checked: String(tcgId) === String(tcg.id),
                      onChange: () => chooseGame(tcg.id),
                    }}
                  />
                ))}
              </>
            )}
          </SheetBody>
        </>

        {/* -------------------------------------------------------- set */}
        <>
          <SheetHeader
            drill
            title="Set"
            backLabel="Back to filters"
            onBack={back}
            ref={(node) => {
              headRefs.current[PANEL.set] = node;
            }}
          />
          <SheetBody>
            {!tcgId ? (
              <>
                {/* The endpoint is per-game, so this is a real dependency and
                    not a UI choice. Say so, and offer the step rather than a
                    dead end. */}
                <Helper>Sets belong to a game. Pick one and its sets appear here.</Helper>
                <Cta variant="ghost" size="sm" block onClick={openPanel(PANEL.game)}>
                  Choose a game
                </Cta>
                {/* `Cta` is always a <button> and this navigates, so it has to
                    be a real link wearing the library's own ghost CTA classes.
                    It leaves the page, which is why it is the fallback and not
                    the path. */}
                <Link
                  className="ds-cta ds-cta--ghost ds-cta--sm ds-cta--block dsa-filtersheet__away"
                  to={routes.SETS}
                >
                  Browse all sets instead
                </Link>
              </>
            ) : (
              <>
                <div className="dsa-filtersheet__search">
                  <SearchField
                    insheet
                    value={setQuery}
                    onChange={(event) => setSetQuery(event.target.value)}
                    placeholder="Search sets"
                    aria-label="Search sets"
                  />
                </div>

                {loadingCatalogs ? (
                  <Helper>Loading sets</Helper>
                ) : catalogs.length === 0 ? (
                  <Helper>No sets listed for this game yet.</Helper>
                ) : (
                  <>
                    <OptionRow
                      control="radio"
                      label="All sets"
                      inputProps={{
                        name: 'catalogGroup',
                        checked: !filters?.catalog_group_id,
                        onChange: () => chooseSet(''),
                      }}
                    />
                    {visibleCatalogs.map((catalog) => (
                      <OptionRow
                        key={catalog.catalog_group_id}
                        control="radio"
                        label={catalog.catalog_group}
                        inputProps={{
                          name: 'catalogGroup',
                          checked:
                            String(filters?.catalog_group_id) ===
                            String(catalog.catalog_group_id),
                          onChange: () => chooseSet(catalog.catalog_group_id),
                        }}
                      />
                    ))}
                    {/* A search that matches nothing has to say so. An empty
                        list under a filled search box reads as a failed load. */}
                    {query && visibleCatalogs.length === 0 ? (
                      <Helper>No set matches that.</Helper>
                    ) : null}
                  </>
                )}
              </>
            )}
          </SheetBody>
        </>

        {/* ----------------------------------------------------- rarity */}
        <>
          <SheetHeader
            drill
            title="Rarity"
            appliedCount={selectedRarities.length}
            backLabel="Back to filters"
            onBack={back}
            ref={(node) => {
              headRefs.current[PANEL.rarity] = node;
            }}
          />
          <SheetBody>
            {!tcgId ? (
              <>
                <Helper>Rarities differ per game. Pick a game to filter by rarity.</Helper>
                <Cta variant="ghost" size="sm" block onClick={openPanel(PANEL.game)}>
                  Choose a game
                </Cta>
              </>
            ) : loadingRarities ? (
              <Helper>Loading rarities</Helper>
            ) : rarities.length === 0 ? (
              <Helper>No rarities available for this game.</Helper>
            ) : (
              rarities.map((rarity) => (
                <OptionRow
                  key={rarity}
                  control="checkbox"
                  label={rarity}
                  inputProps={{
                    checked: selectedRarities.includes(rarity),
                    onChange: () => toggleMulti('rarity', rarity),
                  }}
                />
              ))
            )}
          </SheetBody>
        </>

        {/* -------------------------------------------------- condition */}
        <>
          <SheetHeader
            drill
            title="Condition"
            appliedCount={selectedConditions.length}
            backLabel="Back to filters"
            onBack={back}
            ref={(node) => {
              headRefs.current[PANEL.condition] = node;
            }}
          />
          <SheetBody>
            {CONDITIONS.map((condition) => (
              <OptionRow
                key={condition}
                control="checkbox"
                label={condition}
                inputProps={{
                  checked: selectedConditions.includes(condition),
                  onChange: () => toggleMulti('condition', condition),
                }}
              />
            ))}
          </SheetBody>
        </>
      </SheetDrill>

      {/* The footer sits OUTSIDE the drill, so it does not slide with the
          panels: it is the same two commands wherever you are in the stack, and
          the solid one always means "I am done here". Clear all is disabled
          with nothing applied rather than hidden, because a control that
          appears and disappears under the thumb is worse than a quiet one. */}
      <SheetFooter>
        <CtaRow>
          <Cta
            variant="ghost"
            disabled={appliedCount === 0}
            onClick={() => {
              /* The page's full reset when it supplied one: below the handoff
                 the search box is on the page behind this sheet, so clearing
                 only the facets here left a query still narrowing the results
                 the footer is counting. */
              if (onClearAll) onClearAll();
              else onFilterChange(CLEARED_FILTERS);
              setPanel(PANEL.root);
              /* AND IT CLOSES. Resetting and staying open leaves the buyer
                 looking at an empty form over results they cannot see, with
                 "Show N results" as the only way to find out what the clear
                 did. */
              if (onClose) onClose();
            }}
          >
            Clear all
          </Cta>
          <Cta variant="solid" onClick={onClose}>
            {resultsLabel}
          </Cta>
        </CtaRow>
      </SheetFooter>
    </>
  );
}

export default MarketplaceFilterSheet;
